import React, {
  useCallback, useMemo, Fragment, ReactElement,
} from 'react';
import Menu from '../../../components/menu/menu.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import Tooltip from '../../../components/tooltip/tooltip.component';
import CheckAccess from '../../../components/check-access/check-access.component';
import FlexContainer from '../../../components/flex-container/flex-container.component';
import FilteredLink from '../../../components/filtered-link/filtered-link.component';
import Hint from '../../../components/hint/hint.component';
import ChipLink from '../../../components/chip-link/chip-link.component';
import ActionButton from '../../../components/action-button/action-button.component';
import TextBlock from '../../../components/text-block/text-block.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldMultiSelect from '../../../components/form-field-multi-select/form-field-multi-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import FormFieldPhone from '../../../components/form-field-phone/form-field-phone.component';
import FormFieldUpload from '../../../components/form-field-upload/form-field-upload.component';
import DropdownButton from '../../../components/dropdown-button/dropdown-button.component';
import FieldSeparatedBlock from '../../../components/field-separated-block/field-separated-block.component';
import FileLink from '../../../components/file-link/file-link.component';
import FileListContainer from '../../../components/file-list-container/file-list-container.component';
import MenuItemAddNewItem from '../../../components/menu-item-add-new-item/menu-item-add-new-item.component';
import ImageFileContainer from '../../../components/image-file-container/image-file-container.component';
import TableRowAddNewItem from '../../../components/table-row-add-new-item/table-row-add-new-item.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import SinglePageTab from '../../app/common/single-page-tab/single-page-tab.component';
import RemoveIcon from '../../../icons/remove-icon';
import CancelFilledIcon from '../../../icons/cancel-filled-icon';
import FormatService from '../../../services/format-service';
import FormService from '../../../services/form-service';
import StructureService from '../../../services/structure-service';
import {
  ACTIVITY_ATTACHMENT_LIST_VIEW, ACTIVITY_STATUS_EDIT, CONTACT_CREATE, CONTACT_VIEW, DEAL_CREATE, DEAL_VIEW,
  SEARCH_RESULT_VIEW,
} from '../../../constants/entity/permissions';
import { ACTIVITY_CATEGORY_NEW_DEAL } from '../../../constants/entity/activity-categories';
import { FILE_EXTENSION_LIST_IMAGE } from '../../../constants/system/file-extensions';
import AssetDefaultImage from '../../../static/images/asset-default-image.png';
import PersonDefaultImage from '../../../static/images/person-default-image.png';
import CompanyDefaultImage from '../../../static/images/company-default-image.png';

const ActivitySummary: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const COLUMNS_TOTAL: number = 2;

  const {
    isSaving,
    isSaveDisabled,
    isEditMode,
    isRightDrawerOpen,
    isNewDealFormVisible,
    isNewDealSourceFormVisible,
    isNewDealContactFormVisible,
    formError,
    menuAnchor,
    activityData,
    activityFieldItems,
    activityFieldNewItemIds,
    dealPhotoFile,
    selectedTags,
    selectedDealContacts,
    selectedDealContactGroups,
    assetList,
    dealList,
    tagList,
    contactList,
    contactGroupList,
    contactCompanyList,
    contactPersonList,
    contactPersonInternalList,
    measureUnitList,
    activityTypeList,
    activityStatusList,
    activityInterestLevelList,
    onDealClick,
    onContactClick,
    onTagClick,
    onTagsChange,
    onActivityFieldAddNewItem,
    onActivityFieldDeleteClick,
    onCancelForm,
    onSubmitForm,
    onStatusSelect,
    onOpenStatusMenu,
    onCloseStatusMenu,
    onDealContactsChange,
    onDealContactGroupsChange,
    onDealContactPhoneChange,
    onDealPhotoFileChange,
    onDealPhotoFileDelete,
    onActivityDealSearch,
    onActivityDealSearchClose,
    onDealAssetSearch,
    onDealAssetSearchClose,
    onOpenNewDealForm,
    onOpenNewDealSourceForm,
    onOpenNewDealContactForm,
    onCloseNewDealForm,
    onCloseNewDealSourceForm,
    onCloseNewDealContactForm,
    onContactSearch,
    onContactSearchClose,
    onContactPersonSearch,
    onContactPersonSearchClose,
    onContactCompanySearch,
    onContactCompanySearchClose,
    onTagSearch,
    onTagSearchClose,
  } = props;

  const activityTypeMap: Record<string, ActivityType> = useMemo((): Record<string, ActivityType> => (
    StructureService.getItemMap(activityTypeList, 'name')
  ), [activityTypeList]);
  const measureUnitMap: Record<string, MeasureUnit> = useMemo((): Record<string, MeasureUnit> => (
    StructureService.getItemMap(measureUnitList, 'name')
  ), [measureUnitList]);
  const activityInterestLevelMap: Record<string, ActivityInterestLevel> = useMemo(
    (): Record<string, ActivityInterestLevel> => (
      StructureService.getItemMap(activityInterestLevelList, 'name')
    ),
    [activityInterestLevelList],
  );

  const activityStatusEditPermissions: string[] = useMemo((): string[] => [ACTIVITY_STATUS_EDIT], []);
  const activityAttachmentListViewPermissions: string[] = useMemo((): string[] => [ACTIVITY_ATTACHMENT_LIST_VIEW], []);

  const formPrefix: string = 'activityData';
  const activitySizeMeasureUnit: string = activityData && activityData.sizeType && activityData.sizeType.name
    && measureUnitMap && measureUnitMap[activityData.sizeType.name]
    && (measureUnitMap[activityData.sizeType.name].title || '');
  const activityCategory: string = activityData && activityData.category;
  const dealId: number | null = activityData.deal ? activityData.deal.id : null;

  const menuItemActivityStatusList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityStatusList,
    onClick: !isEditMode ? ((event: React.MouseEvent<Element, MouseEvent>, selectedStatus: ActivityStatus): void => {
      if (selectedStatus.name) {
        onStatusSelect(selectedStatus.name);
      }

      if (onCloseStatusMenu) {
        onCloseStatusMenu(event);
      }
    }) : undefined,
  }), [activityStatusList, isEditMode]);

  const menuItemActivityTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityTypeList,
  }), [activityTypeList]);

  const menuItemDealList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: dealList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [dealList]);

  const menuItemAssetList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'name',
    optionList: assetList,
    optionItemImageKey: 'mainPhoto',
    optionItemImageThumbnailKey: 'mainPhotoThumbnail',
    defaultImage: AssetDefaultImage,
  }), [assetList]);

  const menuItemContactCompanyList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactCompanyList,
    defaultImage: CompanyDefaultImage,
  }), [contactCompanyList]);

  const menuItemContactDealContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: activityCategory === ACTIVITY_CATEGORY_NEW_DEAL
      ? contactPersonInternalList : contactPersonList,
    defaultImage: PersonDefaultImage,
    selectedItemList: selectedDealContacts,
  }), [contactPersonInternalList, contactPersonList, activityCategory, selectedDealContacts]);

  const menuItemContactGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: contactGroupList,
    selectedItemList: selectedDealContactGroups,
  }), [contactGroupList, selectedDealContactGroups]);

  const menuItemMeasureUnitList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: measureUnitList,
  }), [measureUnitList]);

  const menuItemActivityInterestLevelList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: activityInterestLevelList,
  }), [activityInterestLevelList]);

  const menuItemContactList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    withImage: true,
    isExtended: true,
    optionItemLabelKey: 'fullName',
    optionList: contactList,
    defaultImage: CompanyDefaultImage,
  }), [contactList]);

  const menuItemTagList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionItemLabelKey: 'name',
    optionList: tagList,
    selectedItemList: selectedTags,
  }), [tagList, selectedTags]);

  const dealContactsChange: Function = useCallback((contactValues: Contact): void => {
    if (onDealContactsChange) {
      onDealContactsChange(contactValues);
    }
  }, []);

  const dealContactGroupsChange: Function = useCallback((contactGroupValues: ContactGroup[]): void => {
    if (onDealContactGroupsChange) {
      onDealContactGroupsChange(contactGroupValues);
    }
  }, []);

  const dealContactPhoneChange: PhoneChangeHandlerFunction = useCallback((phoneValue?: string): void => {
    if (onDealContactPhoneChange) {
      onDealContactPhoneChange(phoneValue);
    }
  }, []);

  return (
    <SinglePageTab
      isSaving={isSaving}
      isSaveDisabled={isSaveDisabled}
      isEditMode={isEditMode}
      isRightDrawerOpen={isRightDrawerOpen}
      formError={formError}
      onCancelForm={onCancelForm}
      onSubmitForm={onSubmitForm}
    >
      <Table>
        <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
          <TableRow>
            <TableCell>
              Status
              {
                isEditMode && (
                  <RequiredMark />
                )
              }
            </TableCell>
            <TableCell>
              {
                isEditMode ? (
                  <FormFieldSelect
                    isBordered
                    isDisabled={activityStatusList && !activityStatusList.length}
                    name={`${formPrefix}.status`}
                    optionsTotal={activityStatusList && activityStatusList.length}
                  >
                    {menuItemActivityStatusList}
                  </FormFieldSelect>
                ) : (
                  <FlexContainer>
                    <TextBlock>
                      {activityData.statusText}
                    </TextBlock>
                    <CheckAccess permissions={activityStatusEditPermissions}>
                      <DropdownButton onClick={onOpenStatusMenu} />
                      <Menu menuAnchor={menuAnchor} onClose={onCloseStatusMenu}>
                        {menuItemActivityStatusList}
                      </Menu>
                    </CheckAccess>
                  </FlexContainer>
                )
              }
            </TableCell>
          </TableRow>
          {
            (isEditMode || activityData.type) && (
              <TableRow>
                <TableCell>
                  Type
                  {
                    isEditMode && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        isDisabled={activityTypeList && !activityTypeList.length}
                        name={`${formPrefix}.type`}
                        optionsTotal={activityTypeList && activityTypeList.length}
                      >
                        {menuItemActivityTypeList}
                      </FormFieldSelect>
                    ) : (
                      (activityData.type && activityTypeMap[activityData.type]
                        && activityTypeMap[activityData.type].title) || ''
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            ((isEditMode && !isNewDealFormVisible) || !isEditMode) && (
              <TableRow>
                <TableCell>
                  Deal
                  {
                    isEditMode && (
                      <RequiredMark />
                    )
                  }
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        name={`${formPrefix}.dealId`}
                        optionsTotal={dealList && dealList.length}
                        onSearch={onActivityDealSearch}
                        onClose={onActivityDealSearchClose}
                        newItemComponent={(
                          !isNewDealFormVisible && (
                            <MenuItemAddNewItem
                              label="New Deal"
                              createPermission={DEAL_CREATE}
                              onClick={onOpenNewDealForm}
                            />
                          )
                        )}
                      >
                        {menuItemDealList}
                      </FormFieldSelect>
                    ) : (
                      activityData.deal && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.deal.name}
                          imageLink={activityData.deal.mainPhotoThumbnail || activityData.deal.mainPhoto
                            || AssetDefaultImage}
                          permission={DEAL_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onDealClick(event, dealId);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealFormVisible && (
              <TableRow>
                <TableCell>
                  Deal (new)
                </TableCell>
                <TableCell isTextRight>
                  <ActionButton onClick={onCloseNewDealForm}>
                    <CancelFilledIcon />
                  </ActionButton>
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    withAutoFocus={isNewDealFormVisible}
                    name={`${formPrefix}.deal.name`}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Photo
                </TableCell>
                <TableCell>
                  <ImageFileContainer>
                    <FormFieldUpload
                      isImage
                      isDroppable
                      isSmallImage
                      name={`${formPrefix}.deal.mainPhoto`}
                      uploadFile={dealPhotoFile}
                      buttonLabel="New Deal"
                      acceptedExtensionList={FILE_EXTENSION_LIST_IMAGE}
                      defaultPreviewUrl={AssetDefaultImage}
                      onFileChange={onDealPhotoFileChange}
                      onFileDelete={onDealPhotoFileDelete}
                    />
                  </ImageFileContainer>
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Asset
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withSearch
                    withBlankElement
                    isDisabled={assetList && !assetList.length}
                    name={`${formPrefix}.deal.assetId`}
                    optionsTotal={assetList && assetList.length}
                    onSearch={onDealAssetSearch}
                    onClose={onDealAssetSearchClose}
                  >
                    {menuItemAssetList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
            )
          }
          {
            ((isEditMode && !isNewDealSourceFormVisible) || (!isEditMode && activityData.dealSource)) && (
              <TableRow>
                <TableCell>
                  Deal Source
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        name={`${formPrefix}.dealSourceId`}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        newItemComponent={(
                          !isNewDealSourceFormVisible && (
                            <MenuItemAddNewItem
                              label="New Deal Source"
                              createPermission={CONTACT_CREATE}
                              onClick={onOpenNewDealSourceForm}
                            />
                          )
                        )}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    ) : (
                      activityData.dealSource && activityData.dealSource.name && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.dealSource.name}
                          imageLink={activityData.dealSource.photoThumbnail || activityData.dealSource.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.dealSource.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealSourceFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Source (new)
                </TableCell>
                <TableCell isTextRight>
                  <ActionButton onClick={onCloseNewDealSourceForm}>
                    <CancelFilledIcon />
                  </ActionButton>
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealSourceFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Source Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    withAutoFocus={isNewDealSourceFormVisible}
                    name={`${formPrefix}.dealSource.name`}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            isEditMode && isNewDealSourceFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Source Website
                  <Hint
                    title={'Fill in Website field to search and automatically populate other fields '
                      + 'of the company, if it is listed in our dictionary'}
                  />
                </TableCell>
                <TableCell>
                  <FormFieldText
                    isBordered
                    name={`${formPrefix}.dealSource.website`}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.sponsor) && (
              <TableRow>
                <TableCell>
                  Sponsor
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactCompanyList && !contactCompanyList.length}
                        name={`${formPrefix}.sponsorId`}
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    ) : (
                      activityData.sponsor && activityData.sponsor.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.sponsor.fullName}
                          imageLink={activityData.sponsor.photoThumbnail || activityData.sponsor.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.sponsor.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (!isEditMode && activityData.broker)) && (
              <TableRow>
                <TableCell>
                  Broker
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactCompanyList && !contactCompanyList.length}
                        name={`${formPrefix}.brokerId`}
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    ) : (
                      activityData.broker && activityData.broker.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.broker.fullName}
                          imageLink={activityData.broker.photoThumbnail || activityData.broker.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.broker.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.lender) && (
              <TableRow>
                <TableCell>
                  Lender
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactCompanyList && !contactCompanyList.length}
                        name={`${formPrefix}.lenderId`}
                        optionsTotal={contactCompanyList && contactCompanyList.length}
                        onSearch={onContactCompanySearch}
                        onClose={onContactCompanySearchClose}
                      >
                        {menuItemContactCompanyList}
                      </FormFieldSelect>
                    ) : (
                      activityData.lender && activityData.lender.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.lender.fullName}
                          imageLink={activityData.lender.photoThumbnail || activityData.lender.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.lender.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (activityData.dealContacts && !!activityData.dealContacts.length)) && (
              <TableRow>
                <TableCell>
                  Deal Contacts
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        withImage
                        withSearch
                        isDisabled={contactPersonList && !contactPersonList.length}
                        nameKey="fullName"
                        name={`${formPrefix}.dealContactIds`}
                        optionList={contactPersonList}
                        onValuesChange={dealContactsChange}
                        defaultImage={PersonDefaultImage}
                        onSearch={onContactPersonSearch}
                        onClose={onContactPersonSearchClose}
                        newItemComponent={(
                          !isNewDealContactFormVisible && (
                            <MenuItemAddNewItem
                              label="New Deal Contact"
                              createPermission={CONTACT_CREATE}
                              onClick={onOpenNewDealContactForm}
                            />
                          )
                        )}
                      >
                        {menuItemContactDealContactList}
                      </FormFieldMultiSelect>
                    ) : (
                      activityData.dealContacts && !!activityData.dealContacts.length
                      && activityData.dealContacts.map((contactItem: Contact): ReactElement => (
                        <ChipLink
                          isDisabled={isEditMode}
                          key={contactItem.id}
                          label={contactItem.fullName}
                          imageLink={contactItem.photoThumbnail || contactItem.photo || PersonDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, contactItem.id);
                          }}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact (new)
                </TableCell>
                <TableCell isTextRight>
                  <ActionButton onClick={onCloseNewDealContactForm}>
                    <CancelFilledIcon />
                  </ActionButton>
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact First Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.dealContact.firstName`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact Last Name
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.dealContact.lastName`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact Email
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldText isBordered name={`${formPrefix}.dealContact.email`} />
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact Phone
                </TableCell>
                <TableCell>
                  <FormFieldPhone
                    isBordered
                    name={`${formPrefix}.dealContact.phone`}
                    onChange={dealContactPhoneChange}
                  />
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact Group
                  <RequiredMark />
                </TableCell>
                <TableCell>
                  <FormFieldMultiSelect
                    isBordered
                    isDisabled={contactGroupList && !contactGroupList.length}
                    keyField="name"
                    name={`${formPrefix}.dealContact.groupIds`}
                    optionList={contactGroupList}
                    onValuesChange={dealContactGroupsChange}
                  >
                    {menuItemContactGroupList}
                  </FormFieldMultiSelect>
                </TableCell>
              </TableRow>
            )
          }
          {
            isNewDealContactFormVisible && (
              <TableRow>
                <TableCell>
                  Deal Contact Company
                </TableCell>
                <TableCell>
                  <FormFieldSelect
                    isBordered
                    withSearch
                    withBlankElement
                    isDisabled={contactCompanyList && !contactCompanyList.length}
                    name={`${formPrefix}.dealContact.companyId`}
                    optionsTotal={contactCompanyList && contactCompanyList.length}
                    onSearch={onContactCompanySearch}
                    onClose={onContactCompanySearchClose}
                  >
                    {menuItemContactCompanyList}
                  </FormFieldSelect>
                </TableCell>
              </TableRow>
            )
          }
          {
            (!isEditMode && activityData.dealContact && activityData.dealContact.email) && (
              <TableRow>
                <TableCell>
                  Deal Contact Email
                </TableCell>
                <TableCell>
                  <FilteredLink isEmail link={activityData.dealContact.email}>
                    {activityData.dealContact.email}
                  </FilteredLink>
                </TableCell>
              </TableRow>
            )
          }
          {
            (!isEditMode && activityData.dealContact && activityData.dealContact.phone
              && activityData.dealContact.phoneText) && (
              <TableRow>
                <TableCell>
                  Deal Contact Phone
                </TableCell>
                <TableCell>
                  <FilteredLink
                    isPhone
                    link={FormatService.formatCleanIntegerNumberString(activityData.dealContact.phone)}
                  >
                    {activityData.dealContact.phoneText}
                  </FilteredLink>
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.amount) && (
              <TableRow>
                <TableCell>
                  Total Capitalization
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.amount`} />
                    ) : activityData.amount
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.pricing) && (
              <TableRow>
                <TableCell>
                  Price Guidance
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.pricing`} />
                    ) : activityData.pricing
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.size) && (
              <TableRow>
                <TableCell>
                  Size
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FieldSeparatedBlock withOffset>
                        <FormFieldNumber isBordered name={`${formPrefix}.size`} />
                        <FormFieldSelect
                          isBordered
                          withBlankElement
                          isDisabled={measureUnitList && !measureUnitList.length}
                          name={`${formPrefix}.sizeType`}
                          optionsTotal={measureUnitList && measureUnitList.length}
                        >
                          {menuItemMeasureUnitList}
                        </FormFieldSelect>
                      </FieldSeparatedBlock>
                    ) : (
                      activityData.size && `${activityData.size} ${activitySizeMeasureUnit}`
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.ltv) && (
              <TableRow>
                <TableCell>
                  LTV
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber
                        isFloat
                        isPercent
                        isBordered
                        withNoLimit
                        name={`${formPrefix}.ltv`}
                      />
                    ) : activityData.ltv
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.purchasePrice) && (
              <TableRow>
                <TableCell>
                  Purchase Price
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.purchasePrice`} />
                    ) : activityData.purchasePrice
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.capitalExpenditure) && (
              <TableRow>
                <TableCell>
                  CapEx
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.capitalExpenditure`} />
                    ) : activityData.capitalExpenditure
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.oneYearNoi) && (
              <TableRow>
                <TableCell>
                  1 Year NOI
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.oneYearNoi`} />
                    ) : activityData.oneYearNoi
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.yieldOnCost) && (
              <TableRow>
                <TableCell>
                  Yield On Cost
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber
                        isFloat
                        isPercent
                        isBordered
                        withNoLimit
                        name={`${formPrefix}.yieldOnCost`}
                      />
                    ) : activityData.yieldOnCost
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.debtProceeds) && (
              <TableRow>
                <TableCell>
                  Debt Proceeds
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.debtProceeds`} />
                    ) : activityData.debtProceeds
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.requiredEquityAmount) && (
              <TableRow>
                <TableCell>
                  Required Equity
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber isBordered name={`${formPrefix}.requiredEquityAmount`} />
                    ) : activityData.requiredEquityAmount
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.interestLevel) && (
              <TableRow>
                <TableCell>
                  Level of Interest
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withBlankElement
                        isDisabled={activityInterestLevelList && !activityInterestLevelList.length}
                        name={`${formPrefix}.interestLevel`}
                        optionsTotal={activityInterestLevelList && activityInterestLevelList.length}
                      >
                        {menuItemActivityInterestLevelList}
                      </FormFieldSelect>
                    ) : (
                      (activityData.interestLevel && activityInterestLevelMap[activityData.interestLevel]
                        && activityInterestLevelMap[activityData.interestLevel].title) || ''
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.ltc) && (
              <TableRow>
                <TableCell>
                  LTC
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldNumber
                        isFloat
                        isPercent
                        isBordered
                        withNoLimit
                        name={`${formPrefix}.ltc`}
                      />
                    ) : activityData.ltc
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.equityProvider) && (
              <TableRow>
                <TableCell>
                  Equity Provider
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactList && !contactList.length}
                        name={`${formPrefix}.equityProviderId`}
                        optionsTotal={contactList && contactList.length}
                        onSearch={onContactSearch}
                        onClose={onContactSearchClose}
                      >
                        {menuItemContactList}
                      </FormFieldSelect>
                    ) : (
                      activityData.equityProvider && activityData.equityProvider.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.equityProvider.fullName}
                          imageLink={activityData.equityProvider.photoThumbnail || activityData.equityProvider.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.equityProvider.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.investor) && (
              <TableRow>
                <TableCell>
                  Investor
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactList && !contactList.length}
                        name={`${formPrefix}.investorId`}
                        optionsTotal={contactList && contactList.length}
                        onSearch={onContactSearch}
                        onClose={onContactSearchClose}
                      >
                        {menuItemContactList}
                      </FormFieldSelect>
                    ) : (
                      activityData.investor && activityData.investor.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.investor.fullName}
                          imageLink={activityData.investor.photoThumbnail || activityData.investor.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.investor.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.lenderRole) && (
              <TableRow>
                <TableCell>
                  Lender Role
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.lenderRole`} />
                    ) : activityData.lenderRole
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.maxAmount) && (
              <TableRow>
                <TableCell>
                  Max Amount
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.maxAmount`} />
                    ) : activityData.maxAmount
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.loanRecourse) && (
              <TableRow>
                <TableCell>
                  Loan Recourse
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.loanRecourse`} />
                    ) : activityData.loanRecourse
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.additionalParty) && (
              <TableRow>
                <TableCell>
                  Additional Party
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldSelect
                        isBordered
                        withSearch
                        withBlankElement
                        isDisabled={contactList && !contactList.length}
                        name={`${formPrefix}.additionalPartyId`}
                        optionsTotal={contactList && contactList.length}
                        onSearch={onContactSearch}
                        onClose={onContactSearchClose}
                      >
                        {menuItemContactList}
                      </FormFieldSelect>
                    ) : (
                      activityData.additionalParty && activityData.additionalParty.fullName && (
                        <ChipLink
                          isDisabled={isEditMode}
                          label={activityData.additionalParty.fullName}
                          imageLink={activityData.additionalParty.photoThumbnail || activityData.additionalParty.photo
                            || CompanyDefaultImage}
                          permission={CONTACT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onContactClick(event, activityData.additionalParty.id);
                          }}
                        />
                      )
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || activityData.additionalPartyDetails) && (
              <TableRow>
                <TableCell>
                  Additional Party Details
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldText isBordered name={`${formPrefix}.additionalPartyDetails`} />
                    ) : (activityData.additionalPartyDetails)
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            (isEditMode || (activityData.tags && !!Object.keys(activityData.tags).length)) && (
              <TableRow>
                <TableCell>
                  Tags
                </TableCell>
                <TableCell>
                  {
                    isEditMode ? (
                      <FormFieldMultiSelect
                        isBordered
                        isAbleToAdd
                        withSearch
                        isDisabled={tagList && !tagList.length}
                        keyField="name"
                        name={`${formPrefix}.tags`}
                        optionList={tagList}
                        onSearch={onTagSearch}
                        onClose={onTagSearchClose}
                        onValuesChange={onTagsChange}
                      >
                        {menuItemTagList}
                      </FormFieldMultiSelect>
                    ) : (
                      activityData.tags && !!Object.keys(activityData.tags).length
                      && activityData.tags.map((tagItem: Tag): ReactElement => (
                        <ChipLink
                          isDisabled={isEditMode}
                          key={tagItem.id}
                          label={tagItem.name}
                          permission={SEARCH_RESULT_VIEW}
                          onClick={(event: React.MouseEvent<Element, MouseEvent>): void => {
                            onTagClick(event, tagItem.id);
                          }}
                        />
                      ))
                    )
                  }
                </TableCell>
              </TableRow>
            )
          }
          {
            !isEditMode && activityData.attachments && !!activityData.attachments.length && (
              <CheckAccess permissions={activityAttachmentListViewPermissions}>
                <TableRow>
                  <TableCell>
                    Documents
                  </TableCell>
                  <TableCell>
                    <FileListContainer>
                      {
                        activityData.attachments.map((attachmentItem: Attachment): ReactElement => (
                          <FlexContainer isInline key={attachmentItem.id}>
                            <FileLink fileLink={attachmentItem.file} />
                          </FlexContainer>
                        ))
                      }
                    </FileListContainer>
                  </TableCell>
                </TableRow>
              </CheckAccess>
            )
          }
          {
            ((activityFieldItems && Object.values(activityFieldItems)) || [])
              .map((activityFieldItem: ActivityField): ReactElement => {
                const isNewItem: boolean = activityFieldNewItemIds && activityFieldNewItemIds.length
                  ? activityFieldNewItemIds.includes(activityFieldItem.id) : false;
                const formKeyActivityField: string = FormService.getIndexedFormFieldKey(
                  activityFieldItem.id,
                );

                return (
                  <TableRow isActive={isNewItem} key={activityFieldItem.id}>
                    <TableCell>
                      {
                        isEditMode ? (
                          <Fragment>
                            <FormFieldText
                              isHidden
                              name={`${formPrefix}.options[${formKeyActivityField}].id`}
                            />
                            <FormFieldText
                              isBordered
                              name={`${formPrefix}.options[${formKeyActivityField}].name`}
                              placeholder="Name"
                            />
                          </Fragment>
                        ) : activityFieldItem.name
                      }
                    </TableCell>
                    <TableCell>
                      {
                        isEditMode ? (
                          <FlexContainer>
                            <FormFieldText
                              isBordered
                              name={`${formPrefix}.options[${formKeyActivityField}].value`}
                              placeholder="Value"
                            />
                            <Tooltip title="Delete">
                              <ActionButton
                                isInline
                                withOffset
                                withNoPadding
                                onClick={(): void => {
                                  if (onActivityFieldDeleteClick) {
                                    onActivityFieldDeleteClick(activityFieldItem.id);
                                  }
                                }}
                              >
                                <RemoveIcon />
                              </ActionButton>
                            </Tooltip>
                          </FlexContainer>
                        ) : activityFieldItem.value
                      }
                    </TableCell>
                  </TableRow>
                );
              })
          }
          {
            isEditMode && (
              <TableRowAddNewItem colSpan={COLUMNS_TOTAL + 1} onClick={onActivityFieldAddNewItem} />
            )
          }
        </TableBody>
      </Table>
    </SinglePageTab>
  );
});

export default ActivitySummary;
